import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  Skeleton,
} from "@mui/material";

import _ from "lodash";
import { SmartTable, insertIf } from "src/components/SmartTable";
import { formatMoneyString } from "src/utility/utilityFunctions";

import BudgetValue from "@features/budgets/BudgetValue";
import { useOrderSetBudgetSpend } from "@features/budgets/OrderSetBudgetSpendContext";
import { useCouponBudgetSpendQuery } from "@features/budgets/queries/budgetSpendQueries";
import useOrderSetBudgetStatus from "@features/budgets/useOrderSetBudgetStatus";
import { FaIcon, PaperTooltip, Type } from "@features/ui";
import { BudgetSpend, BudgetWithoutSpend } from "@models/Budget";

import CompareCosts from "../CompareCosts";
import { useCurrentOrderSet } from "../orderSet/data/CurrentOrderSetContext";

const money = {
  align: "right",
  render: formatMoneyString,
} as const;

const OrderSetBudgetImpact = ({ compact }: { compact?: boolean }) => {
  const { orderSetSpendPerBudget, orderSetSpendPerBudgetCoop, isCoopOrderSet } =
    useCurrentOrderSet();
  const { budgetSpends, isFetching } = useOrderSetBudgetSpend();
  const { budgets: allBudgets, remainingAfterOrder } =
    useOrderSetBudgetStatus();

  const budgets = compact
    ? allBudgets.filter((b) => orderSetSpendPerBudget[b.id] > 0)
    : allBudgets;

  const {
    hasCouponBudgetSpend,
    isCouponSpendsUpToDate,
    isFetching: isFetchingCouponSpends,
    getCouponRedemptionAndFees,
  } = useCouponBudgetSpendQuery();

  const spendValue = (
    budgetId: string,
    getActualValue: (spend: BudgetSpend) => number
  ) => {
    if (!budgetSpends?.[budgetId]) {
      return <Skeleton variant="text" width={80} className="ml-auto" />;
    }
    const actualValue = getActualValue(budgetSpends[budgetId]);

    return <BudgetValue value={actualValue} className="font-medium" />;
  };

  return (
    <SmartTable
      rows={budgets}
      isFetching={isFetching}
      maxHeight={400}
      columns={[
        {
          id: "brandName",
          label: "Budget",
          render: (brandName, budget) => (
            <PaperTooltip
              title={budget.externalId}
              placement="top-start"
              disableHoverListener={isCoopOrderSet}
            >
              <span className="cursor-help">{brandName}</span>
            </PaperTooltip>
          ),
        },
        { id: "subStateName", label: "State" },
        { id: "year", label: "Year" },
        {
          id: "_availableAmount",
          label: "Available",
          ...money,
          render: (_, budget) =>
            spendValue(budget.id, (spend) => +spend.actualAvailableAmount),
        },
        {
          id: "_orderTotal",
          label: "Est. Order Total",
          ...money,
          render: (_, budget) => (
            <CompareCosts
              cost={
                isCoopOrderSet
                  ? orderSetSpendPerBudgetCoop[budget.id]
                  : orderSetSpendPerBudget[budget.id]
              }
              comparedTo={orderSetSpendPerBudget[budget.id]}
            />
          ),
        },
        ...insertIf<BudgetWithoutSpend>(hasCouponBudgetSpend, {
          id: "_couponTotal",
          label: "Coupon Redemption & Fees",
          ...money,
          render: (_, budget) => {
            if (!budget.isCoupon) return null;
            const couponSpend = getCouponRedemptionAndFees(budget.id);
            if (couponSpend === null || isFetchingCouponSpends) {
              return <Skeleton variant="text" width={80} className="ml-auto" />;
            }
            return (
              <span className="flex items-center justify-end gap-2">
                {!isCouponSpendsUpToDate && (
                  <FaIcon icon="refresh" className="text-neutral-300" />
                )}
                <BudgetValue value={couponSpend} />
              </span>
            );
          },
        }),
        {
          id: "availableAfterOrder",
          label: "Remaining After Order",
          ...money,
          render: (_, budget) => {
            const remaining = remainingAfterOrder(budget);
            if (remaining === null) {
              return <Skeleton variant="text" width={80} className="ml-auto" />;
            }
            return <BudgetValue value={remaining} className="font-medium" />;
          },
        },
      ]}
      noResultsText="No Budgets on this Order Set"
    />
  );
};

export default OrderSetBudgetImpact;

export const OrderSetBudgetImpactAccordion = () => {
  const { orders } = useCurrentOrderSet();
  const hasBudgets = orders.some((o) =>
    o.orderVariants.some((ov) => ov.budgets.length > 0)
  );
  if (!hasBudgets) return null;
  return (
    <Accordion
      defaultExpanded
      component={Card}
      slotProps={{ transition: { unmountOnExit: true } }}
      className="!rounded-xl"
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Type.H2>Budget Impact</Type.H2>
      </AccordionSummary>
      <AccordionDetails className="p-0">
        <OrderSetBudgetImpact />
      </AccordionDetails>
    </Accordion>
  );
};
